import { useCallback, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import EDCheckbox from '../Checkboxes/EDCheckbox';
import EDTypography from '../Typography/EDTypography';
import EDIcon from '../Icon/EDIcon';
import { ITreeSectionProps } from './EDTreeSelect';
import {
  updateChecked,
  updateOpen,
  getSublistCheckStatus,
  ITreeSection,
  SectionCheckStatus,
} from './TreeSelectUtils';
import EDHighlightedText from '../Autocomplete/Components/EDHighlightedText';
import { getTextParts } from '../Autocomplete/Utils/AutocompleteUtils';
import { EDListItemButton } from '../List/Components/EDListItem';

interface IProps<T> extends ITreeSectionProps<T> {
  section: ITreeSection<T>;
  sectionIndex: number;
}

const useStyles = makeStyles()(theme => ({
  cursor: {
    cursor: 'pointer',
  },
  headerContainer: {
    display: 'flex',
    flex: '0 1 auto',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionHeaderContainer: {
    display: 'flex',
    flex: '0 1 auto',
    alignItems: 'center',
    overflow: 'hidden',
  },
  listItem: {},
  hoverButtons: {
    opacity: 0,
    transition: 'opacity 100ms ease',
    '&listItem:hover &': {
      opacity: 1,
    },
  },
  listItemEndContainer: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  fontSize: {
    fontSize: '0.875rem',
    wordBreak: 'break-word',
  },
  fontBold: {
    fontWeight: theme.typography.fontWeightBold,
  },
  itemContainer: {
    paddingTop: 0,
    paddingBottom: 0,
    whiteSpace: 'normal',
    display: 'block',
  },
}));

function EDTreeSelectSection<T>(props: IProps<T>) {
  const { sectionsUpdate, onCheck, disableSectionsClick } = props;
  const { classes, cx } = useStyles();
  const sectionChecked = getSublistCheckStatus(props.section, props.isChecked);
  const onCheckSection = useCallback(
    (value: boolean) => {
      const { checkedSections, updatedSections } = updateChecked(
        props.section,
        props.sections,
        value
      );
      if (onCheck) {
        sectionsUpdate(onCheck(updatedSections, checkedSections, value));
      } else {
        sectionsUpdate(updatedSections);
      }
    },
    [props.section, props.sections, onCheck, sectionsUpdate]
  );
  const updateOpenSection = useCallback(() => {
    sectionsUpdate(updateOpen(props.section, props.sections));
  }, [sectionsUpdate, props.section, props.sections]);

  const disableSectionClick = useMemo(() => {
    if (!props.section.subList) {
      return false;
    }
    if (typeof disableSectionsClick === 'function') {
      return disableSectionsClick(props.section);
    }
    return Boolean(disableSectionsClick);
  }, [disableSectionsClick, props.section]);

  return (
    <EDListItemButton
      disableTabbing
      className={cx('arrowMoveTarget', classes.listItem, {
        [classes.itemContainer]: props.sections.length > 1,
      })}
      onClick={updateOpenSection}
      data-testid={props.section.name}
      dense
      disableGutters
    >
      <div className={cx(classes.headerContainer)}>
        <div className={classes.sectionHeaderContainer}>
          <EDCheckbox
            flexShrink={0}
            checkboxSpacing={{ padding: 1 }}
            label=""
            onCheck={!disableSectionClick ? onCheckSection : undefined}
            checked={sectionChecked === SectionCheckStatus.FullyChecked}
            indeterminate={
              sectionChecked === SectionCheckStatus.PartiallyChecked
            }
            disabled={disableSectionClick || props.section.disabled?.value}
            disabledReason={
              disableSectionClick
                ? 'Nod ej valbar'
                : props.section.disabled?.tooltip
            }
            tooltip={
              props.section.subList
                ? sectionChecked === SectionCheckStatus.FullyChecked
                  ? 'Ta bort alla'
                  : 'Välj alla'
                : undefined
            }
            noClickPropagation={
              props.section.open ||
              sectionChecked === SectionCheckStatus.FullyChecked
            }
          />
          <EDTypography
            className={cx(classes.fontSize, {
              [classes.fontBold]:
                !!props.parentSectionBoldText && !!props.section.subList,
            })}
            variant="body1"
          >
            {props.section.subList ? props.section.name : null}
            {!props.section.subList && !props.section.selected && (
              <>
                <EDHighlightedText
                  parts={getTextParts(
                    props.section.name,
                    props.searchValue || ''
                  )}
                />
                {props.section.disabled?.labelSuffix && (
                  <span>{props.section.disabled.labelSuffix}</span>
                )}
              </>
            )}
          </EDTypography>
        </div>
        <div className={classes.listItemEndContainer}>
          {!props.showListItemButtons ||
            (props.showListItemButtons?.(props.section) && (
              <div
                className={cx({
                  [classes.hoverButtons]: props.hoverChecklistButtons?.(
                    props.section
                  ),
                })}
              >
                {props.renderListItemButtons?.(props.section)}
              </div>
            ))}
          {props.section.subList && (
            <EDIcon
              marginRight={1}
              paddingRight={2}
              iconType={
                props.section.open ? 'arrow_drop_up' : 'arrow_drop_down'
              }
            />
          )}
        </div>
      </div>
    </EDListItemButton>
  );
}

export default EDTreeSelectSection;
